import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { useInView } from '../hooks/useInView';

const IMAGES = {
  mould: 'https://images.unsplash.com/photo-1676646606766-f8325cac7faf?crop=entropy&cs=srgb&fm=jpg&q=85&w=900',
  decoration: 'https://static.prod-images.emergentagent.com/jobs/13ebbb87-c8f1-4957-a008-fa06bcff1c9a/images/173cae390679637cab339600ecf6a4ab55bf8efebf1e90465301b8af6c0e67fe.png',
  printing: 'https://images.unsplash.com/photo-1760804876161-ba0337e998fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=900',
  factory: 'https://images.unsplash.com/photo-1727292485794-925866ec2372?crop=entropy&cs=srgb&fm=jpg&q=85&w=900',
};

const SERVICES = [
  {
    id: 'tooling',
    overline: 'Service 01',
    title: 'Custom Mould Making & Tooling',
    subtitle: 'From Concept to Production-Ready',
    desc: 'Our in-house tooling division offers end-to-end custom mould development. Whether you have a design concept or a finished sketch, our engineers transform your vision into precision-crafted moulds — ready for production at scale.',
    features: ['2D & 3D design capability', 'Prototype development', 'Steel & aluminium tooling', 'Multi-cavity moulds', 'Tooling modification & repair', 'Short lead times'],
    image: IMAGES.mould,
    imageRight: false,
  },
  {
    id: 'decoration',
    overline: 'Service 02',
    title: 'Decoration Finishes',
    subtitle: 'Elevating Packaging into Art',
    desc: 'Our decoration services transform standard packaging into luxury brand experiences. From metallic finishes to intricate foiling, we offer a comprehensive range of decorative techniques to make your packaging truly stand out on shelf.',
    features: ['Metalizing (vacuum metallization)', 'Spray coating (matte, gloss, soft-touch)', 'Hot stamping (gold, silver, custom)', 'Foiling & embossing', 'Custom colour matching', 'Multi-layer decoration'],
    image: IMAGES.decoration,
    imageRight: true,
  },
  {
    id: 'printing',
    overline: 'Service 03',
    title: 'Precision Printing',
    subtitle: 'Sharp. Vibrant. Durable.',
    desc: "Our printing solutions deliver crisp, vibrant results that reflect your brand's identity with precision. Using advanced UV silk screen and pad printing technologies, we achieve exceptional colour fidelity on any surface geometry.",
    features: ['UV silk screen printing (up to 6 colours)', 'Pad printing (curved & irregular surfaces)', 'Solvent-based & UV inks', 'Pantone colour matching', 'High-resolution fine detail work', 'Batch consistency guaranteed'],
    image: IMAGES.printing,
    imageRight: false,
  },
];

export default function Services() {
  return (
    <main style={{ paddingTop: '80px', backgroundColor: '#FAF9F6', minHeight: '100vh' }}>
      {/* Banner */}
      <section className="py-20" style={{ backgroundColor: '#0B1B3D' }} data-testid="services-banner">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs tracking-[0.3em] uppercase mb-3 font-medium" style={{ color: '#D4AF37' }}>Beyond Packaging</p>
          <h1 className="font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}>
            Value-Added Services
          </h1>
          <p className="text-base font-light mt-4 max-w-xl" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Comprehensive in-house capabilities that transform packaging into powerful brand assets — from tooling to finishing.
          </p>
        </div>
      </section>

      {/* Service Sections */}
      {SERVICES.map(svc => (
        <ServiceSection key={svc.id} service={svc} />
      ))}

      {/* Process Section */}
      <ProcessSection />

      {/* CTA */}
      <section className="py-24" style={{ backgroundColor: '#0B1B3D' }} data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs tracking-[0.3em] uppercase mb-4 font-medium" style={{ color: '#D4AF37' }}>Partner With Us</p>
          <h2 className="font-light mb-6 text-white" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
            Let's Create Something Exceptional
          </h2>
          <p className="text-base font-light mb-10 max-w-lg mx-auto" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Our experts are ready to discuss your decoration and printing requirements. Get in touch for a tailored proposal.
          </p>
          <Link
            to="/contact"
            className="inline-block px-10 py-4 text-xs tracking-[0.2em] uppercase transition-all hover:opacity-85"
            style={{ backgroundColor: '#D4AF37', color: '#0B1B3D', fontWeight: '600' }}
            data-testid="services-cta-button"
          >
            Request a Consultation
          </Link>
        </div>
      </section>
    </main>
  );
}

function ServiceSection({ service }) {
  const [ref, inView] = useInView();
  const textContent = (
    <div
      className="flex flex-col justify-center"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(2rem)',
        transition: 'opacity 0.7s ease-out, transform 0.7s ease-out',
      }}
    >
      <p className="text-xs tracking-[0.3em] uppercase mb-2 font-medium" style={{ color: '#D4AF37' }}>{service.overline}</p>
      <h2 className="font-light mb-2 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0B1B3D', fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)' }}>
        {service.title}
      </h2>
      <p className="text-sm mb-4 tracking-wide italic" style={{ color: '#4A4F5A' }}>{service.subtitle}</p>
      <p className="text-base font-light leading-relaxed mb-7" style={{ color: '#4A4F5A' }}>{service.desc}</p>
      <ul className="space-y-2.5">
        {service.features.map(f => (
          <li key={f} className="flex items-center gap-3 text-sm font-light" style={{ color: '#1A1D24' }}>
            <CheckCircle size={15} style={{ color: '#D4AF37', flexShrink: 0 }} />
            {f}
          </li>
        ))}
      </ul>
    </div>
  );

  const imageContent = (
    <div
      className="relative overflow-hidden"
      style={{
        aspectRatio: '4/3',
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(2rem)',
        transition: 'opacity 0.7s ease-out 0.15s, transform 0.7s ease-out 0.15s',
      }}
    >
      <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
    </div>
  );

  return (
    <section
      id={service.id}
      className="py-20 md:py-28"
      style={{ backgroundColor: service.imageRight ? '#FFFFFF' : '#FAF9F6' }}
      data-testid={`service-section-${service.id}`}
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-14 items-center">
          {service.imageRight ? <>{textContent}{imageContent}</> : <>{imageContent}{textContent}</>}
        </div>
      </div>
    </section>
  );
}

function ProcessSection() {
  const [ref, inView] = useInView();
  const steps = [
    { num: '01', title: 'Brief & Consultation', desc: 'Share your vision. Our team understands your brand requirements.' },
    { num: '02', title: 'Design & Sampling', desc: 'We create samples and renderings for your approval.' },
    { num: '03', title: 'Tooling & Production', desc: 'Moulds are crafted and production begins with strict QC.' },
    { num: '04', title: 'Finishing & Delivery', desc: 'Decoration and printing applied. Delivered to your door.' },
  ];

  return (
    <section className="py-20" style={{ backgroundColor: '#0B1B3D' }} data-testid="process-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div ref={ref} style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(2rem)', transition: 'all 0.7s ease' }}>
          <p className="text-xs tracking-[0.3em] uppercase mb-3 font-medium" style={{ color: '#D4AF37' }}>How We Work</p>
          <h2 className="font-light text-white mb-12" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
            Our Process
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
          {steps.map((step, idx) => (
            <div
              key={step.num}
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(2rem)',
                transition: `opacity 0.6s ease ${idx * 120}ms, transform 0.6s ease ${idx * 120}ms`,
              }}
              data-testid={`process-step-${step.num}`}
            >
              <div className="text-4xl font-light mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#D4AF37' }}>{step.num}</div>
              <h3 className="font-medium mb-2 text-white" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.15rem' }}>{step.title}</h3>
              <p className="text-sm font-light leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
